"use client";

import React, { useEffect, useRef } from "react";
import { Search } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

const DEBOUNCE_MS = 300;
const MIN_SEARCH_CHARS = 3;

interface AdminListSearchProps {
  value: string;
  onValueChange: (value: string) => void;
  onSearchChange: (effective: string) => void;
  placeholder: string;
  className?: string;
  inputClassName?: string;
  iconClassName?: string;
  size?: "default" | "hero";
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}

export function AdminListSearch({
  value,
  onValueChange,
  onSearchChange,
  placeholder,
  className = "relative w-full sm:min-w-[200px] sm:w-72",
  inputClassName,
  iconClassName,
  size = "default",
  onKeyDown,
}: AdminListSearchProps) {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHero = size === "hero";
  const defaultInputClass = isHero
    ? "pl-10 h-12 py-3 text-sm md:text-base bg-white border-0 rounded-lg shadow-lg font-medium w-full placeholder-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary"
    : "pl-8 h-8 text-xs w-full";

  useEffect(() => {
    const trimmed = value.trim();
    if (trimmed === "") {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      onSearchChange("");
      return;
    }
    timeoutRef.current = setTimeout(() => {
      timeoutRef.current = null;
      const effective =
        trimmed.length >= MIN_SEARCH_CHARS ? trimmed : "";
      onSearchChange(effective);
    }, DEBOUNCE_MS);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [value, onSearchChange]);

  return (
    <div className={cn("relative", className)}>
      <Search className={cn(
        "absolute top-1/2 -translate-y-1/2",
        isHero ? "left-3 h-4 w-4 md:h-5 md:w-5" : "left-2.5 h-3.5 w-3.5",
        iconClassName ?? (isHero ? "text-primary" : "text-muted-foreground")
      )} />
      <Input
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        className={inputClassName ?? defaultInputClass}
      />
    </div>
  );
}